import React, { useState, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Bell, Search, Menu, Loader2, RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

const DashboardLayout = ({ isAuthLoading }) => {
  const queryClient = useQueryClient();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const GlobalLoader = ({ message }) => (
    <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
      <Loader2 className="w-10 h-10 text-accent-primary animate-spin" />
      <p className="text-text-muted animate-pulse">{message}</p>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-bg-primary">
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
      
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="h-16 border-b border-border-color bg-bg-primary/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-4 flex-1">
            <button 
              className="md:hidden text-text-secondary hover:text-text-primary"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>
            <div className="relative w-full max-w-sm hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
              <input 
                type="text" 
                placeholder="Search across modules..." 
                className="w-full bg-bg-secondary border border-border-color rounded-full py-2 pl-10 pr-4 text-sm text-text-primary focus:outline-none focus:border-accent-primary transition-colors"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4 md:gap-6">
            <button 
              onClick={async () => {
                queryClient.invalidateQueries();
                // Clean Service Worker caches (PWA)
                if ('serviceWorker' in navigator) {
                  const registrations = await navigator.serviceWorker.getRegistrations();
                  for (let registration of registrations) {
                    await registration.unregister();
                  }
                }
                // Force hard reload
                window.location.reload();
              }}
              className="relative text-text-secondary hover:text-text-primary transition-colors group"
              title="Clean Cache & Refresh"
            >
              <RefreshCw size={18} className="group-active:animate-spin" />
            </button>
            <button className="relative text-text-secondary hover:text-text-primary transition-colors">
              <Bell size={20} />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-status-danger rounded-full"></span>
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          {isAuthLoading ? (
            <GlobalLoader message="Initializing secure workspace..." />
          ) : (
            <Suspense fallback={<GlobalLoader message="Loading module..." />}>
              <Outlet />
            </Suspense>
          )}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
